import type { Notebook, NotebookCell } from "./notebookSnapshot"
import { reportCoverageNotes } from "./reportCoverageNotes"
import type { Notebook as ReportNotebook } from "./reportTypes"

export interface ExportSection {
  heading: string
  body: string
  /** Render separately so Markdown syntax in source labels or URLs cannot break links. */
  sourceLinks?: Array<{ label: string; url: string }>
  cells: Array<{ id: string; cell: NotebookCell }>
}

export interface NotebookExport {
  title: string
  summary: string
  sections: ExportSection[]
  caveats: string[]
  savedAt?: string
  revision?: number
}

/** The same visible document, never prompts, research steps or action receipts. */
export function savedNotebookExport(notebook: Notebook): NotebookExport {
  const document = notebook.document
  const cells = notebook.snapshot.cells
  const excluded = new Set(document?.excluded_cells ?? [])
  const embedded = new Set(
    document?.sections.flatMap((section) => section.embed_cells) ?? [],
  )
  const evidence = (indexes: number[]) =>
    indexes.flatMap((index) => {
      const cell = cells[index]
      return !excluded.has(index) &&
        cell &&
        ["chart", "table"].includes(cell.kind)
        ? [{ id: String(index), cell }]
        : []
    })
  const sections: ExportSection[] = document
    ? document.sections.map((section) => ({
        heading: section.heading,
        body: section.body,
        cells: evidence(section.embed_cells),
      }))
    : cells.flatMap((cell) =>
        cell.kind === "text"
          ? [{ heading: "Analysis", body: cell.text, cells: [] }]
          : [],
      )
  const appendix = evidence(
    cells.map((_, index) => index).filter((index) => !embedded.has(index)),
  )
  if (appendix.length) {
    sections.push({ heading: "Additional results", body: "", cells: appendix })
  }
  return {
    title: document?.title ?? notebook.title,
    summary: document?.summary ?? "",
    sections,
    caveats: document?.caveats ?? [],
    savedAt: notebook.updated_at,
  }
}

export function reportNotebookExport(notebook: ReportNotebook): NotebookExport {
  const assignedSourceIds = new Set(
    notebook.sections.flatMap((section) => section.evidence_ids),
  )
  const sections: ExportSection[] = notebook.sections.map((section) => ({
    heading: section.title,
    body: section.body,
    sourceLinks: notebook.source_refs
      .filter((source) => section.evidence_ids.includes(source.id))
      .map(({ label, url }) => ({ label, url })),
    cells: section.chart
      ? [
          {
            id: section.id,
            cell: {
              kind: "chart",
              title: section.chart.title,
              spec: section.chart,
            },
          },
        ]
      : [],
  }))
  const unassignedSources = notebook.source_refs
    .filter((source) => !assignedSourceIds.has(source.id))
    .map(({ label, url }) => ({ label, url }))
  if (unassignedSources.length) {
    sections.push({
      heading: "Additional sources",
      body: "",
      sourceLinks: unassignedSources,
      cells: [],
    })
  }
  return {
    title: notebook.title,
    summary: notebook.summary,
    sections,
    caveats: [
      ...new Set([
        ...(notebook.caveats ?? []),
        ...reportCoverageNotes(notebook.coverage),
      ]),
    ],
    // A later revision cannot use the original creation time as its save time.
    savedAt:
      notebook.revisions?.find(
        (revision) => revision.revision === notebook.revision,
      )?.created_at ??
      (notebook.revision === 1 ? notebook.created_at : undefined),
    revision: notebook.revision,
  }
}

export function safeExportUrl(
  value: string,
  origin: string,
): string | undefined {
  try {
    const url = new URL(value, origin)
    return ["https:", "http:"].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}

/** Workspace AGENTS.md displays exported times in Pacific; label PST/PDT explicitly. */
export function formatNotebookSavedAt(value: string): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    timeZoneName: "short",
  })
}

export function notebookPdfFilename(title: string): string {
  const name = title
    .normalize("NFKC")
    .replace(/[^\p{L}\p{N}._ -]/gu, "")
    .trim()
    .slice(0, 100)
  return `${name && name !== "." && name !== ".." ? name : "Sofia notebook"}.pdf`
}
